/**
 * Subuser permission catalogue. This list is the single source of truth: panel route guards,
 * daemon JWT scopes and the daemon's own checks (generated Go file) all derive from it.
 */
export const PERMISSIONS = {
  websocket: {
    description: 'Connect to the server websocket.',
    keys: { connect: 'Receive console output and live stats.' },
  },
  control: {
    description: 'Control the server power state and console.',
    keys: {
      console: 'Send commands to the server console.',
      start: 'Start the server.',
      stop: 'Stop or kill the server.',
      restart: 'Restart the server.',
    },
  },
  user: {
    description: 'Manage subusers of the server.',
    keys: {
      create: 'Create subusers.',
      read: 'View subusers and their permissions.',
      update: 'Modify subusers.',
      delete: 'Remove subusers.',
    },
  },
  file: {
    description: 'Manage server files.',
    keys: {
      create: 'Create and upload files and folders.',
      read: 'List directories and download files.',
      'read-content': 'View the contents of files.',
      update: 'Modify existing files and folders.',
      delete: 'Delete files and folders.',
      archive: 'Create and extract archives.',
      sftp: 'Connect over SFTP (still bound by the other file permissions).',
    },
  },
  backup: {
    description: 'Manage server backups.',
    keys: {
      create: 'Create backups.',
      read: 'List backups.',
      delete: 'Delete backups.',
      download: 'Download backups.',
      restore: 'Restore backups (overwrites server files).',
    },
  },
  allocation: {
    description: 'Manage network allocations.',
    keys: {
      read: 'View allocations.',
      create: 'Add allocations.',
      update: 'Change the primary allocation and notes.',
      delete: 'Remove allocations.',
    },
  },
  startup: {
    description: 'Manage startup parameters.',
    keys: {
      read: 'View startup variables.',
      update: 'Modify startup variables.',
      'docker-image': 'Change the docker image.',
    },
  },
  database: {
    description: 'Manage server databases.',
    keys: {
      create: 'Create databases.',
      read: 'View databases.',
      update: 'Rotate database passwords.',
      delete: 'Delete databases.',
      view_password: 'View database passwords.',
    },
  },
  schedule: {
    description: 'Manage schedules.',
    keys: {
      create: 'Create schedules.',
      read: 'View schedules.',
      update: 'Modify schedules and tasks.',
      delete: 'Delete schedules.',
    },
  },
  settings: {
    description: 'Manage server settings.',
    keys: {
      rename: 'Rename the server.',
      reinstall: 'Reinstall the server.',
    },
  },
  activity: {
    description: 'View server activity.',
    keys: { read: 'View the activity log.' },
  },
} as const;

type Catalogue = typeof PERMISSIONS;
export type Permission = {
  [G in keyof Catalogue]: `${G & string}.${keyof Catalogue[G]['keys'] & string}`;
}[keyof Catalogue];

export const ALL_PERMISSIONS: readonly Permission[] = Object.entries(PERMISSIONS).flatMap(
  ([group, def]) => Object.keys(def.keys).map((key) => `${group}.${key}` as Permission),
);

const permissionSet: ReadonlySet<string> = new Set(ALL_PERMISSIONS);

export function isPermission(value: string): value is Permission {
  return permissionSet.has(value);
}

/** Owners and admins hold the wildcard; subusers hold an explicit list. */
export function hasPermission(held: readonly string[], wanted: Permission): boolean {
  return held.includes('*') || held.includes(wanted);
}
